import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import {
  AddActionItemToAgreementDocument,
  PerformanceGoalsEmployeePoliciesDocument,
  PerformanceProcessAgreementsActionItemListFragment,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  ActionItem,
  ActionItemShell,
} from 'modules/performance_process/components/agreements/ActionItem'
import { ActionInput } from 'modules/performance_process/components/common/ActionInput'

export const ActionItemList: React.FC<{
  agreement: PerformanceProcessAgreementsActionItemListFragment
}> = ({ agreement }) => {
  const {
    id: agreementId,
    locked,
    actionItemsInfo: actionItems,
    target,
    goalMetrics,
    _policies: policies,
  } = agreement

  const targetEmployeeId = target.access?.employee?.id
  if (!targetEmployeeId) throw new Error('Target is required')

  const { data: permissions, loading: loadingPermissions } = useQuery(
    PerformanceGoalsEmployeePoliciesDocument,
    {
      variables: { employeeId: targetEmployeeId },
    }
  )

  const [newActionItemText, setNewActionItemText] = useState('')

  const [addActionItemToAgreement, { loading }] = useMutation(AddActionItemToAgreementDocument, {
    extractorFn: (data) => data.performanceMutations.addPerformanceAgreementActionItem,
  })

  const handleAddActionItem = async () => {
    await addActionItemToAgreement({
      variables: {
        agreementId: agreementId,
        text: newActionItemText.trim(),
      },
    })
    setNewActionItemText('')
  }

  const canAddActionItems = policies?.addActionItems?.can
  const canCreateMetrics =
    !loadingPermissions && permissions?.goals?.employee?._policies?.createMetric?.can

  return (
    <Box gap="s24">
      <Box>
        <AnimatePresence presenceAffectsLayout>
          {actionItems.map((actionItem, index) => (
            <ActionItem
              index={index}
              agreementId={agreementId}
              actionItem={actionItem}
              key={actionItem.id}
              employeeId={targetEmployeeId}
              canCreateMetrics={canCreateMetrics ?? false}
              metric={goalMetrics.find(
                (m) => m.performanceAgreementActionItemUuid === actionItem.id
              )}
            />
          ))}
        </AnimatePresence>
        {!locked && canAddActionItems && (
          <ActionItemShell index={actionItems.length}>
            <ActionInput
              placeholder={i18n.t('performance_process.agreements.action_items.add_action')}
              refocus
              disabled={loading}
              onChange={(value) => setNewActionItemText(value)}
              onSubmit={handleAddActionItem}
              value={newActionItemText}
            />
          </ActionItemShell>
        )}
      </Box>
    </Box>
  )
}
